import React, { useEffect, useState } from 'react';
import api from '../../../api/axios';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, Divider, Menu, MenuItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useCurrency } from '../../../hooks/useCurrency';
import { useDepositWithdrawModal } from './DepositWithdrawModalContext';
import { setWalletState, toggleEarningsVisibility } from '../../../store/slices/authSlice';
import { useWallet } from '../../../hooks/useWallet';
import { setRate } from '../../../store/slices/currencySlice';
import { rateFromScaled } from '../../utils/UnitConversion.utils';
import { Eye, EyeOff } from 'lucide-react';

export type WalletDto = {
  currency: string;
  walletStatus: 'AVAILABLE' | 'WAITING_FOR_KYC' | 'FROZEN' | 'BANNED' | 'DELETED' | 'INACTIVE' | null;
  walletTotalAmountMinor: number;
  walletTradeableAmountMinor: number;
  walletWithdrawableAmountMinor: number;
  walletOnHoldSaleAmountMinor: number;
  walletOnHoldPurchaseAmountMinor: number;
  walletOnHoldDepositAmountMinor: number;
  walletTotalRevenueMinor: number;
};

export type WithdrawalFeeDTO = {
  feeType: 'CREDIT_CARD_EUR' | 'CREDIT_CARD_GLOBAL' | 'OPEN_BANKING';
  rateScaled: number;
  fixedMinor: number;
  minAmountMinor: number;
  maxAmountMinor: number;
};

const Wallet: React.FC = () => {
  const dispatch = useDispatch();
  const { formatAmount, currency } = useCurrency();
  const { symbol } = useSelector((state: RootState) => state.currency);
  const isHidden = useSelector((state: RootState) => state.auth.isEarningsHidden);

  const { tradeable, withdrawable, onHoldSale, onHoldPurchase } = useWallet();
  const { openModal } = useDepositWithdrawModal();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openDropdown = Boolean(anchorEl);

  useEffect(() => {
    const loadWallet = async () => {
      const { data } = await api.get<WalletDto>('/wallet/balance');
      dispatch(setWalletState(data));

      try {
        const rateRes = await api.get(`/currency/${currency}`);
        if (rateRes.data?.rateScaled) {
          dispatch(setRate(rateFromScaled(rateRes.data.rateScaled)));
        }
      } catch (err) {
        console.error('Failed to load currency rate', err);
      }
    };
    loadWallet();
  }, [dispatch, currency]);

  const handleDisplayClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isHidden) {
      dispatch(toggleEarningsVisibility());
    } else {
      setAnchorEl(e.currentTarget);
    }
  };

  const renderValue = (val: number) => isHidden ? '••••' : formatAmount(val);

  return (
      <div className="flex items-center justify-between bg-gray-400 dark:bg-neutral-700 text-white rounded-md shadow-md min-w-[120px]">
        <div className="flex items-center gap-1 cursor-pointer hover:opacity-80 px-2" onClick={handleDisplayClick}>
          <div className="flex items-center gap-1 font-medium">
            <div className="min-w-16 max-w-28">
              <div className="flex gap-0.5 font-semibold text-md leading-none">
                <div>{symbol}</div>
                <div>{renderValue(tradeable)}</div>
              </div>
              <div className="text-[10px] font-semibold leading-none dark:text-gray-400 text-light">Tradeable</div>
            </div>
            <Divider orientation="vertical" flexItem />
            <div className="min-w-16 max-w-28 pl-1 flex items-center gap-0">
              <div className="pr-1">
                <div className="flex gap-0.5 font-semibold text-md leading-none">
                  <div>{symbol}</div>
                  <div>{renderValue(onHoldPurchase)}</div>
                </div>
                <div className="text-[10px] font-semibold leading-none dark:text-gray-400 text-light">On hold</div>
              </div>
              <KeyboardArrowDownIcon fontSize="small" />
            </div>
          </div>
        </div>

        <Button
            onClick={() => openModal('deposit')}
            variant="contained"
            className="w-10"
            sx={{ minWidth: 44, width: 44, height: 40, borderRadius: '0px 6px 6px 0px' }}
        >
          <AddIcon />
        </Button>

        <Menu
            anchorEl={anchorEl}
            open={openDropdown}
            onClose={() => setAnchorEl(null)}
            disableScrollLock={true}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 2.5,
                  borderRadius: '8px',
                  border: '1px solid #404040',
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 18,
                    width: 12,
                    height: 12,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: '1px solid #404040',
                    borderTop: '1px solid #404040',
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={() => {
            dispatch(toggleEarningsVisibility());
            setAnchorEl(null);
          }}>
            <div className="flex items-center gap-3 w-full text-xs font-bold uppercase tracking-wider text-gray-500">
              {isHidden ? <Eye size={16} /> : <EyeOff size={16} />}
              {isHidden ? 'Show Balances' : 'Hide Balances'}
            </div>
          </MenuItem>

          <Divider />

          <MenuItem>
            <div className="flex justify-between w-full text-sm font-semibold min-w-[200px]">
              <div>Tradeable</div>
              <div className="flex gap-1">
                <span>{symbol}</span>
                <span>{renderValue(tradeable)}</span>
              </div>
            </div>
          </MenuItem>

          <MenuItem>
            <div className="flex justify-between w-full text-sm font-semibold">
              <div>Withdrawable</div>
              <div className="flex gap-1">
                <span>{symbol}</span>
                <span>{renderValue(withdrawable)}</span>
              </div>
            </div>
          </MenuItem>

          <MenuItem>
            <div className="flex justify-between w-full text-sm font-semibold">
              <div>On Hold Sale</div>
              <div className="flex gap-1">
                <span>{symbol}</span>
                <span>{renderValue(onHoldSale)}</span>
              </div>
            </div>
          </MenuItem>

          <MenuItem>
            <div className="flex justify-between w-full text-sm font-semibold">
              <div>On Hold Purchase</div>
              <div className="flex gap-1">
                <span>{symbol}</span>
                <span>{renderValue(onHoldPurchase)}</span>
              </div>
            </div>
          </MenuItem>
        </Menu>
      </div>
  );
};

export default Wallet;
